import React, { useState, useEffect } from "react";
import { RotateCw } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { useRef } from "react";

export default function MemberCard({ user, adminExport = false, cardId }) {
  const [isFlipped, setIsFlipped] = useState(false);
  const [avatar, setAvatar] = useState(null);

  const logo = "/images/aleoa.jpg";
  const bg = "/images/bg.png";
  async function downloadPDF() {
    const container = document.getElementById(`pc-export-${user.member_id}`);

    if (!container) {
      alert("Carte PC introuvable");
      return;
    }

    const front = container.querySelector('[data-side="front"]');
    const back = container.querySelector('[data-side="back"]');

    if (!front || !back) {
      alert("Recto ou verso introuvable");
      return;
    }

    const oldTransform = back.style.transform;
    back.style.transform = "none";

    const pdf = new jsPDF({
      orientation: "landscape",
      unit: "px",
      format: [800, 450],
    });

    const frontCanvas = await html2canvas(front, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: null,
      ignoreElements: (el) => el.classList?.contains("no-pdf"),
    });

    pdf.addImage(frontCanvas.toDataURL("image/png"), "PNG", 0, 0, 800, 450);

    const backCanvas = await html2canvas(back, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: null,
      ignoreElements: (el) => el.classList?.contains("no-pdf"),
    });

    pdf.addPage();
    pdf.addImage(backCanvas.toDataURL("image/png"), "PNG", 0, 0, 800, 450);

    back.style.transform = oldTransform;

    pdf.save(`carte-${user.member_id}.pdf`);
  }

  useEffect(() => {
    setAvatar(user.avatar || "/images/default-avatar.png");
  }, [user]);

  return (
    <div className="flex flex-col items-center mt-8 w-full">
      <button
        onClick={downloadPDF}
        className="mb-4 bg-green-600 text-white px-6 py-3 rounded-xl font-bold shadow no-pdf"
      >
        📄 Télécharger la carte (PDF – format PC)
      </button>

      <div
        className="
    hidden md:block
    w-[800px] h-[450px]
    relative rounded-2xl shadow-2xl overflow-hidden"
        style={{ perspective: "1000px" }}
      >
        <div
          className="relative w-full h-full duration-700"
          style={{
            transformStyle: "preserve-3d",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            data-side="front"
            id={`card-front-${cardId || user.member_id}`}
            className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute top-5 right-5 flex items-center gap-3">
              <img src={logo} className="w-14 h-14 rounded-full shadow" />
              <div className="text-right leading-tight">
                <p className="font-bold text-blue-700 text-sm">ASSOCIATION</p>
                <p className="font-black text-base">ALEOA</p>
              </div>
            </div>

            <div className="flex flex-row items-center gap-8 p-10">
              <div className="flex flex-col items-center">
                <img
                  src={avatar}
                  className="w-36 h-40 object-cover rounded-xl border-4 border-blue-700 shadow-lg"
                />
              </div>

              <div className="flex-1">
                <h2 className="text-2xl font-black text-blue-900">
                  {user.nom.toUpperCase()} {user.prenom.toUpperCase()}
                </h2>

                <p className="text-gray-700 text-sm break-all">{user.email}</p>

                <div className="mt-3 bg-white/70 px-4 py-3 rounded-lg text-sm font-semibold shadow space-y-1">
                  <p className="text-blue-600 font-semibold">
                    ID :{" "}
                    <span className="text-slate-900 font-bold">
                      {user.member_id}
                    </span>
                  </p>

                  <p className="text-blue-600 font-semibold">
                    Téléphone :{" "}
                    <span className="text-slate-900 font-bold">
                      {user.telephone || "—"}
                    </span>
                  </p>

                  <p className="text-blue-600 font-semibold">
                    Date de naissance :{" "}
                    <span className="text-slate-900 font-bold">
                      {user.birthday || "—"}
                    </span>
                  </p>

                  <p className="text-blue-600 font-semibold">
                    Fonction :{" "}
                    <span className="text-green-700 font-bold">
                      {user.fonction || "Membre Actif"}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-12 bg-blue-700 flex items-center justify-between px-6">
              <p className="text-black italic text-sm font-bold">
                CARTE MEMBRE ALEOA
              </p>

              <button
                onClick={() => setIsFlipped(true)}
                className="bg-white/20 p-2 rounded-full text-white no-pdf"
              >
                <RotateCw size={20} />
              </button>
            </div>
          </div>

          <div
            data-side="back"
            id={`card-back-${cardId || user.member_id}`}
            className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="pt-4 px-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src={logo} className="w-16 h-16 rounded-full shadow-md" />
                <div>
                  <h2 className="text-blue-900 font-black text-xl">
                    ASSOCIATION ALEOA
                  </h2>
                  <p className="text-gray-700 text-xs">
                    Informations officielles
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center">
                <QRCodeCanvas
                  value={`https://aleoa.vercel.app/presence?mid=${user.member_id}`}
                  size={90}
                  bgColor="transparent"
                  fgColor="#0a2a71"
                />

                <p className="text-blue-900 text-xs font-semibold mt-1">SCAN</p>
              </div>
            </div>

            <div className="mt-6 px-10 flex items-start justify-between">
              <div className="bg-white/70 px-6 py-5 rounded-xl shadow-lg text-sm space-y-2 border border-blue-200 w-[55%]">
                <p className="font-semibold text-blue-700">
                  Adresse :{" "}
                  <span className="text-gray-900">Oulmes – Maroc</span>
                </p>
                <p className="font-semibold text-blue-700">
                  Email :{" "}
                  <span className="text-gray-900">
                    associationaleoa@gmail.com
                  </span>
                </p>
                <p className="font-semibold text-blue-700">
                  Site : <span className="text-gray-900">aleoa.vercel.app</span>
                </p>
              </div>

              <div className="flex items-center ml-6">
                <img
                  src="/images/sign.png"
                  alt="Signature"
                  className="w-72 opacity-95"
                />
              </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-12 bg-blue-700 flex items-center justify-between px-6">
              <p className="text-black italic text-sm font-bold">
                CARTE MEMBRE ALEOA
              </p>
              <button
                onClick={() => setIsFlipped(false)}
                className="bg-white/20 p-2 rounded-full text-white no-pdf"
              >
                <RotateCw size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div
        id={`pc-export-${user.member_id}`}
        className="
    w-[800px] h-[450px]
    absolute
    -left-[9999px] top-0
    opacity-0
    pointer-events-none"
        style={{ perspective: "1000px" }}
      >
        <div
          className="relative w-full h-full duration-700"
          style={{
            transformStyle: "preserve-3d",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            data-side="front"
            id={`card-front-${cardId || user.member_id}`}
            className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="absolute top-5 right-5 flex items-center gap-3">
              <img src={logo} className="w-14 h-14 rounded-full shadow" />
              <div className="text-right leading-tight">
                <p className="font-bold text-blue-700 text-sm">ASSOCIATION</p>
                <p className="font-black text-base">ALEOA</p>
              </div>
            </div>

            <div className="flex flex-row items-center gap-8 p-10">
              <div className="flex flex-col items-center">
                <img
                  src={avatar}
                  className="w-36 h-40 object-cover rounded-xl border-4 border-blue-700 shadow-lg"
                />
              </div>

              <div className="flex-1">
                <h2 className="text-2xl font-black text-blue-900">
                  {user.nom.toUpperCase()} {user.prenom.toUpperCase()}
                </h2>

                <p className="text-gray-700 text-sm break-all">{user.email}</p>

                <div className="mt-3 bg-white/70 px-4 py-3 rounded-lg text-sm font-semibold shadow space-y-1">
                  <p className="text-blue-600 font-semibold">
                    ID :{" "}
                    <span className="text-slate-900 font-bold">
                      {user.member_id}
                    </span>
                  </p>

                  <p className="text-blue-600 font-semibold">
                    Téléphone :{" "}
                    <span className="text-slate-900 font-bold">
                      {user.telephone || "—"}
                    </span>
                  </p>

                  <p className="text-blue-600 font-semibold">
                    Date de naissance :{" "}
                    <span className="text-slate-900 font-bold">
                      {user.birthday || "—"}
                    </span>
                  </p>

                  <p className="text-blue-600 font-semibold">
                    Fonction :{" "}
                    <span className="text-green-700 font-bold">
                      {user.fonction || "Membre Actif"}
                    </span>
                  </p>
                </div>
              </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-12 bg-blue-700 flex items-center justify-between px-6">
              <p className="text-black italic text-sm font-bold">
                CARTE MEMBRE ALEOA
              </p>

              <button
                onClick={() => setIsFlipped(true)}
                className="bg-white/20 p-2 rounded-full text-white no-pdf"
              >
                <RotateCw size={20} />
              </button>
            </div>
          </div>

          <div
            data-side="back"
            id={`card-back-${cardId || user.member_id}`}
            className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="pt-4 px-10 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <img src={logo} className="w-16 h-16 rounded-full shadow-md" />
                <div>
                  <h2 className="text-blue-900 font-black text-xl">
                    ASSOCIATION ALEOA
                  </h2>
                  <p className="text-gray-700 text-xs">
                    Informations officielles
                  </p>
                </div>
              </div>

              <div className="flex flex-col items-center">
                <QRCodeCanvas
                  value={`https://aleoa.vercel.app/presence?mid=${user.member_id}`}
                  size={90}
                  bgColor="transparent"
                  fgColor="#0a2a71"
                />

                <p className="text-blue-900 text-xs font-semibold mt-1">SCAN</p>
              </div>
            </div>

            <div className="mt-6 px-10 flex items-start justify-between">
              <div className="bg-white/70 px-6 py-5 rounded-xl shadow-lg text-sm space-y-2 border border-blue-200 w-[55%]">
                <p className="font-semibold text-blue-700">
                  Adresse :{" "}
                  <span className="text-gray-900">Oulmes – Maroc</span>
                </p>
                <p className="font-semibold text-blue-700">
                  Email :{" "}
                  <span className="text-gray-900">
                    associationaleoa@gmail.com
                  </span>
                </p>
                <p className="font-semibold text-blue-700">
                  Site : <span className="text-gray-900">aleoa.vercel.app</span>
                </p>
              </div>

              <div className="flex items-center ml-6">
                <img
                  src="/images/sign.png"
                  alt="Signature"
                  className="w-72 opacity-95"
                />
              </div>
            </div>

            <div className="absolute bottom-0 left-0 w-full h-12 bg-blue-700 flex items-center justify-between px-6">
              <p className="text-black italic text-sm font-bold">
                CARTE MEMBRE ALEOA
              </p>
              <button
                onClick={() => setIsFlipped(false)}
                className="bg-white/20 p-2 rounded-full text-white no-pdf"
              >
                <RotateCw size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* =======================================================
           VERSION MOBILE
      ======================================================== */}
      <div
        className="
          md:hidden          
          w-full max-w-sm
          h-[500px]
          relative rounded-2xl shadow-2xl overflow-hidden
          mt-4
        "
        style={{ perspective: "1000px" }}
      >
        <div
          className="relative w-full h-full duration-700"
          style={{
            transformStyle: "preserve-3d",
            transform: isFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
          }}
        >
          <div
            className="absolute inset-0 rounded-2xl overflow-hidden"
            style={{
              backfaceVisibility: "hidden",
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex justify-end p-4">
              <img src={logo} className="w-12 h-12 rounded-full shadow" />
            </div>

            <div className="flex flex-col items-center mt-4">
              <img
                src={avatar}
                className="w-32 h-36 object-cover rounded-xl border-4 border-blue-700 shadow-lg"
              />
            </div>

            <div className="mt-5 text-center px-4">
              <h2 className="text-xl font-black text-blue-900">
                {user.nom.toUpperCase()} {user.prenom.toUpperCase()}
              </h2>

              <p className="text-gray-800 text-sm break-all">{user.email}</p>

              <div className="mt-3 bg-white/70 px-4 py-2 rounded-lg shadow text-xs font-semibold space-y-1">
                <p className="text-blue-600 font-semibold">
                  ID :{" "}
                  <span className="text-slate-900 font-bold">
                    {user.member_id}
                  </span>
                </p>

                <p className="text-blue-600 font-semibold">
                  Téléphone :{" "}
                  <span className="text-slate-900 font-bold">
                    {user.telephone || "—"}
                  </span>
                </p>

                <p className="text-blue-600 font-semibold">
                  Date de naissance :{" "}
                  <span className="text-slate-900 font-bold">
                    {user.birthday || "—"}
                  </span>
                </p>

                <p className="text-blue-600 font-semibold">
                  Fonction :{" "}
                  <span className="text-green-700 font-bold">
                    {user.fonction || "Membre Actif"}
                  </span>
                </p>
              </div>
            </div>

            <div className="absolute mt-3 bottom-0 w-full h-12 bg-blue-700 flex items-center justify-between px-6">
              <p className="text-black italic text-sm font-bold">
                CARTE MEMBRE ALEOA
              </p>

              <button
                onClick={() => setIsFlipped(true)}
                className="bg-white/20 p-2 rounded-full text-white"
              >
                <RotateCw size={18} />
              </button>
            </div>
          </div>

          <div
            className="absolute inset-0 rounded-2xl overflow-hidden flex flex-col"
            style={{
              backfaceVisibility: "hidden",
              transform: "rotateY(180deg)",
              backgroundImage: `url(${bg})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
            }}
          >
            <div className="flex-1 flex items-center justify-center px-3 pt-4">
              <div className="w-full max-w-xs text-center flex flex-col items-center">
                <img
                  src={logo}
                  alt="Logo"
                  className="w-14 h-14 rounded-full shadow-md"
                />

                <h2 className="text-blue-900 font-black text-base mt-2">
                  ASSOCIATION ALEOA
                </h2>
                <p className="text-gray-700 text-xs">
                  Informations officielles
                </p>

                <div className="mt-4 flex items-center justify-center gap-3 w-full">
                  <QRCodeCanvas
                    value={`https://aleoa.vercel.app/presence?mid=${user.member_id}`}
                    size={64}
                    bgColor="transparent"
                    fgColor="#0a2a71"
                  />

                  <img
                    src="/images/sign.png"
                    alt="Signature"
                    className="w-32 opacity-95"
                  />
                </div>

                <p className="text-blue-900 text-[11px] font-semibold mt-2">
                  SCAN
                </p>
              </div>
            </div>

            <div className="px-4 pb-4">
              <div
                className="
      bg-white/90
      px-4 py-3
      rounded-xl
      shadow
      border border-blue-200
      text-xs
      space-y-2
    "
              >
                <div className="flex gap-2">
                  <span className="font-semibold text-blue-700">Adresse :</span>
                  <span className="text-gray-900">Oulmes – Maroc</span>
                </div>

                <div className="flex gap-2">
                  <span className="font-semibold text-blue-700">Email :</span>
                  <span className="text-gray-900 break-all">
                    associationaleoa@gmail.com
                  </span>
                </div>

                <div className="flex gap-2">
                  <span className="font-semibold text-blue-700">Site :</span>
                  <span className="text-gray-900 break-all">
                    aleoa.vercel.app
                  </span>
                </div>
              </div>
            </div>

            <div className="h-12 bg-blue-700 flex items-center justify-between px-6">
              <div className="w-full flex items-center justify-between">
                <p className="text-black italic text-sm font-bold">
                  CARTE MEMBRE ALEOA
                </p>

                <button
                  onClick={() => setIsFlipped(false)}
                  className="bg-white/20 p-2 rounded-full text-white"
                >
                  <RotateCw size={18} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
